import { PrioritizeListItem } from './prioritizelistItem.model';
import { DayDate } from '@deltager/organizers/admin/core/date-format/dayDate';
import { PriceType } from './price-type.enum';
import { PriceInstalmentSettings } from './price-instalment-settings.model';
import { PriceSplitPayment, PriceSplitPaymentSettings } from './price-split-payment.model';
import {
  areArraysEqual,
  areObjectsEqual,
  arePropertiesEqual,
} from '@deltager/organizers/admin/shared/utils/object.utils';
import { getSumOfNumberArray } from '@deltager/organizers/admin/shared/SharedUtilMethods';
import { PriceAgeSettings } from './price-age-settings.model';
import { PriceBreakDownSettings } from './price-breakdown-settings.model';

export class PriceBreakDownElement {
  id: number;
  title: string;
  value: number;
  isActive: boolean;
  vatCategoryId?: number;
  productId?: number;

  constructor(
    title: string,
    value: number,
    isActive: boolean,
    vatCategoryId?: number,
    productId?: number,
    id: number = 0,
  ) {
    this.title = title;
    this.value = value;
    this.isActive = isActive;
    this.vatCategoryId = vatCategoryId;
    this.productId = productId;
    this.id = id;
  }

  static fromJson(item: PriceBreakDownElement): PriceBreakDownElement {
    return new PriceBreakDownElement(
      item.title,
      item.value,
      item.isActive,
      item.vatCategoryId,
      item.productId,
      item.id,
    );
  }

  equals(item: PriceBreakDownElement): boolean {
    return (
      this.title === item.title &&
      this.value === item.value &&
      this.isActive === item.isActive &&
      this.vatCategoryId === item.vatCategoryId &&
      this.productId === item.productId
    );
  }
}

export class Price implements PrioritizeListItem {
  id?: number;
  priceType: PriceType;
  name: string;
  active: boolean;
  isBreakDownPrice?: boolean;
  priceBreakDownElements?: PriceBreakDownElement[];
  value: number;
  vat?: number;
  availableTicketsCount: number;
  registered: number;
  placesTaken: number;
  availableFrom?: any;
  availableTill?: any;
  uiId: number;
  priority: number;
  isDisabledForDependencySelect?: boolean;
  hasAttributeDependencies?: boolean;
  numberOfWaitingListRegistrations: number;
  ageLimit: number;
  ageLimitDateFrom?: Date | string;
  ageLimitDateTo?: Date | string;
  ageLimitMin?: number;
  ageLimitMax?: number;
  childPrices?: Price[];
  instalmentSettings: PriceInstalmentSettings | null;
  splitPayments: PriceSplitPayment[];
  forMembers: boolean;

  constructor() {
    this.id = 0;
    this.name = '';
    this.active = true;
    this.value = 0;
    this.priority = undefined;
    this.vat = 0;
    this.availableTicketsCount = 0;
    this.registered = 0;
    this.availableFrom = undefined;
    this.availableTill = undefined;
    this.instalmentSettings = null;
    this.splitPayments = [];
  }

  static fromJson(value: any): Price {
    const price = new Price();

    return Object.assign(price, value);
  }

  static BuildDefaultPrice(defaultValues: PriceDefaultValues, uiId: number, priority: number): Price {
    const price = new Price();
    price.name = defaultValues.name;
    price.value = defaultValues.value;
    price.uiId = uiId;
    price.priority = priority;
    price.vat = 0;
    price.instalmentSettings = defaultValues.instalmentSettings;
    price.splitPayments = defaultValues.splitPayments;
    if (price.splitPayments.length > 0) {
      price.value = getSumOfNumberArray(price.splitPayments.map(p => p.priceValue));
    }

    return price;
  }

  public static getAgeSettings(price: Price): PriceAgeSettings {
    return {
      ageLimit: price.ageLimit,
      ageLimitDateFrom: price.ageLimitDateFrom,
      ageLimitDateTo: price.ageLimitDateTo,
      ageLimitMin: price.ageLimitMin,
      ageLimitMax: price.ageLimitMax,
    };
  }

  public static getSplitPaymentSettings(price: Price): PriceSplitPaymentSettings | null {
    return price.instalmentSettings
      ? {
          enableChargingPastPayments: price.instalmentSettings.enableChargingPastPayments,
          splitPayments: price.splitPayments,
        }
      : null;
  }

  public static getPriceBreakDownSettings(price: Price): PriceBreakDownSettings {
    return {
      isBreakDownPrice: price.isBreakDownPrice,
      priceBreakDownElements: price.priceBreakDownElements,
    };
  }

  equals(price: Price): boolean {
    return (
      arePropertiesEqual(this.name, price.name) &&
      arePropertiesEqual(this.active, price.active) &&
      arePropertiesEqual(this.value, price.value) &&
      arePropertiesEqual(this.vat, price.vat) &&
      arePropertiesEqual(this.availableTicketsCount, price.availableTicketsCount) &&
      arePropertiesEqual(this.registered, price.registered) &&
      DayDate.isEqual(this.availableFrom, price.availableFrom) &&
      DayDate.isEqual(this.availableTill, price.availableTill) &&
      // this.uiId === price.uiId &&
      arePropertiesEqual(this.priority, price.priority) &&
      this.ageLimit === price.ageLimit &&
      DayDate.isEqual(this.ageLimitDateFrom, price.ageLimitDateFrom) &&
      DayDate.isEqual(this.ageLimitDateTo, price.ageLimitDateTo) &&
      arePropertiesEqual(this.ageLimitMin, price.ageLimitMin) &&
      arePropertiesEqual(this.ageLimitMax, price.ageLimitMax) &&
      arePropertiesEqual(this.isBreakDownPrice, price.isBreakDownPrice) &&
      areObjectsEqual(this.instalmentSettings, price.instalmentSettings) &&
      areArraysEqual(this.splitPayments, price.splitPayments) &&
      this.priceBreakDownElements.length === price.priceBreakDownElements.length &&
      this.priceBreakDownElements.every((elem, i) => {
        const existingBreakDown = price.priceBreakDownElements[i];

        if (!existingBreakDown) {
          return false;
        }

        const priceBreakDown = PriceBreakDownElement.fromJson(elem);

        return priceBreakDown.equals(existingBreakDown);
      }) &&
      arePropertiesEqual(this.forMembers, price.forMembers)
    );
  }

  copy(): Price {
    const price = new Price();
    price.id = 0;
    price.isBreakDownPrice = this.isBreakDownPrice;

    price.priceBreakDownElements = this.priceBreakDownElements.map(
      e => new PriceBreakDownElement(e.title, e.value, e.isActive, e.vatCategoryId, e.productId),
    );

    price.name = this.name;
    price.value = this.value;
    price.active = this.active;
    price.availableFrom = this.availableFrom;
    price.availableTill = this.availableTill;
    price.availableTicketsCount = this.availableTicketsCount;
    price.priority = undefined;
    price.uiId = undefined;
    price.vat = this.vat;
    price.registered = 0;
    price.ageLimit = this.ageLimit;
    price.ageLimitMin = this.ageLimitMin;
    price.ageLimitMax = this.ageLimitMax;
    price.ageLimitDateFrom = this.ageLimitDateFrom;
    price.ageLimitDateTo = this.ageLimitDateTo;
    price.forMembers = this.forMembers;

    return price;
  }
}

export class PriceDefaultValues {
  public name: string;
  public instalmentSettings: PriceInstalmentSettings | null;
  public splitPayments: PriceSplitPayment[];
  public value: number;

  constructor(parameters: Partial<PriceDefaultValues>) {
    Object.assign(this, parameters);
  }
}
